// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-87 — TanStack Query hooks for the SaaS-admin pharmacy settings: module entitlement (Profile + CareRx
// tabs) and the per-pharmacy feature settings (CareRx tab). E-02: components never call api.* directly.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { listPharmacyModules, setPharmacyModule } from '../../api/client'
import {
  getCareRxWeightUnit, listCareRxFeatures, listMyEditableCareRxFeatures, setCareRxFeature, setMyCareRxFeature,
  type FeatureSettingsResponse, type FeatureWireValue, type MyEditableFeaturesResponse,
} from '../../api/pharmacyFeatures'
import type { WeightUnit } from '../../lib/measurements'

export const pharmacySettingsKeys = {
  modules: (pharmacyId: string) => ['admin', 'pharmacy', pharmacyId, 'modules'] as const,
  carerxFeatures: (pharmacyId: string) => ['admin', 'pharmacy', pharmacyId, 'carerx-features'] as const,
  myCarerxFeatures: ['carerx', 'my-feature-settings'] as const,
  weightUnit: ['carerx', 'weight-unit'] as const,
}

/** Module entitlement for one pharmacy — the Profile tab's Modules card and the CareRx tab share this cache. */
export function usePharmacyModules(pharmacyId: string) {
  return useQuery({ queryKey: pharmacySettingsKeys.modules(pharmacyId), queryFn: () => listPharmacyModules(pharmacyId) })
}

export function useSetPharmacyModule(pharmacyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, enabled, trialDays }: { key: string; enabled: boolean; trialDays?: number }) =>
      setPharmacyModule(pharmacyId, key, { enabled, trial_days: trialDays }),
    onSuccess: () => qc.invalidateQueries({ queryKey: pharmacySettingsKeys.modules(pharmacyId) }),
  })
}

export function useCareRxFeatures(pharmacyId: string) {
  return useQuery({ queryKey: pharmacySettingsKeys.carerxFeatures(pharmacyId), queryFn: () => listCareRxFeatures(pharmacyId) })
}

/** Saves ONE setting; the cached row is replaced with what the server stored (Default/Overridden, who, when). */
export function useSetCareRxFeature(pharmacyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: FeatureWireValue }) => setCareRxFeature(pharmacyId, key, value),
    onSuccess: saved => {
      qc.setQueryData<FeatureSettingsResponse>(pharmacySettingsKeys.carerxFeatures(pharmacyId), prev => prev && {
        ...prev, features: prev.features.map(f => (f.key === saved.key ? saved : f)),
      })
    },
  })
}

/** D-CRX-87b A6: the CareRx settings this pharmacy may change itself (CareRx admin). */
export function useMyEditableCareRxFeatures() {
  return useQuery({ queryKey: pharmacySettingsKeys.myCarerxFeatures, queryFn: listMyEditableCareRxFeatures })
}

export function useSetMyCareRxFeature() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: FeatureWireValue }) => setMyCareRxFeature(key, value),
    onSuccess: saved => {
      qc.setQueryData<MyEditableFeaturesResponse>(pharmacySettingsKeys.myCarerxFeatures, prev => prev && {
        ...prev, features: prev.features.map(f => (f.key === saved.key ? saved : f)),
      })
    },
  })
}

/** D-CRX-88: the unit CareRx shows and takes weight in for the signed-in user's pharmacy. Until it loads, or when
 *  the read fails: 'lb' — today's behaviour. Storage is ounces either way. */
export function useCareRxWeightUnit(): WeightUnit {
  const q = useQuery({ queryKey: pharmacySettingsKeys.weightUnit, queryFn: getCareRxWeightUnit, staleTime: 60_000 })
  return q.data ?? 'lb'
}
